import { Component, DestroyRef, inject, ViewChild } from '@angular/core';
import { Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HasUnsavedChanges } from '../../../../guards/confirm-unsaved-changes.guard';
import { AssignableParty } from '../contacts/components/add-department-contact/models/assignable-party';
import { NewOrganisationPick } from './models/organisations';
import { Contact } from '../contacts/models/contacts';
import { OrganisationsListComponent } from './components/organisations-list/organisations-list.component';
import { OrganisationDetailComponent } from './components/organisation-detail/organisation-detail.component';
import { PageHeaderComponent } from '../../../../components/page-header/page-header.component';
import { AdminTabsComponent } from '../admin-tabs/admin-tabs.component';
import { ContactsService } from '../contacts/services/contacts.service';

// Master–detail is a state switch on this one component, not a nested route — the PRD is
// explicit that a route change here would defeat the point (losing the list's filter/scroll
// state every time an admin opens an organisation). See DEV-1192-organisations-view.md
// "Navigation & Placement".
@Component({
  selector: 'app-organisations',
  standalone: true,
  imports: [OrganisationsListComponent, OrganisationDetailComponent, PageHeaderComponent, AdminTabsComponent],
  templateUrl: './organisations.component.html',
  styleUrl: './organisations.component.scss'
})
export class OrganisationsComponent implements HasUnsavedChanges {
  @ViewChild(OrganisationDetailComponent) detail?: OrganisationDetailComponent;
  @ViewChild(OrganisationsListComponent) list?: OrganisationsListComponent;

  view: 'list' | 'detail' = 'list';
  selectedPartyNumber: string | null = null;
  // Survives the list component being destroyed/recreated on every list ↔ detail switch — see
  // OrganisationsListComponent.initialSearchTerm.
  listSearchTerm = '';
  // Set only for the "Add organisation" flow — the detail screen builds an empty, unpersisted
  // draft from this rather than calling GET (that endpoint 404s for a party with no assignments
  // yet — see organisations-design-spec.md §1.2).
  newOrgParty: AssignableParty | null = null;
  // Handoff for the same flow — the /supportContacts/search request the "Add organisation"
  // popover already started in parallel with its unassigned-parties fetch (see
  // AddOrganisationPopoverComponent), so the detail screen's People/Departments pickers can reuse
  // it instead of firing a second request. Opening an existing organisation still fetches contacts
  // fresh on its own — this is only populated for the new-organisation path.
  newOrgContacts$: Observable<Contact[]> | null = null;

  // Badge counts for app-admin-tabs (Contacts/Organisations tab strip above the list/detail
  // switch). organisationsCount comes from the list's own search response (via countChanged, so
  // it stays populated even after the list is destroyed while Detail is open); contactsCount has
  // no equivalent here, so it's fetched once, purely for the badge.
  contactsCount: number | null = null;
  organisationsCount: number | null = null;

  private contactsService = inject(ContactsService);
  private destroyRef = inject(DestroyRef);

  ngOnInit() {
    this.contactsService
      .getAllContacts()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(response => (this.contactsCount = response.data.length));
  }

  // routerLink on the already-active "Organisations" tab is a no-op (same URL, no navigation) —
  // this is the fallback that lets it act as a "back to listing" while the detail view is open.
  // goBack() owns the confirm-discard-if-dirty flow itself, same as the breadcrumb link.
  onTabReselected(tab: 'contacts' | 'organisations') {
    if (tab === 'organisations' && this.view === 'detail') {
      this.detail?.goBack();
    }
  }

  openDetail(partyNumber: string) {
    this.selectedPartyNumber = partyNumber;
    this.newOrgParty = null;
    this.newOrgContacts$ = null;
    this.view = 'detail';
  }

  openNewOrganisation(pick: NewOrganisationPick) {
    this.newOrgParty = pick.party;
    this.newOrgContacts$ = pick.contacts$;
    this.selectedPartyNumber = null;
    this.view = 'detail';
  }

  // The detail component confirms discarding unsaved changes itself (it owns the dirty state and
  // the confirm dialog) before ever emitting this — see OrganisationDetailComponent.goBack().
  backToList(refresh: boolean) {
    this.view = 'list';
    this.selectedPartyNumber = null;
    this.newOrgParty = null;
    this.newOrgContacts$ = null;
    if (refresh) {
      this.list?.reload();
    }
  }

  // Covers browser back/refresh and navigating to a different admin tab while the detail screen
  // is dirty — see guards/confirm-unsaved-changes.guard.ts, wired as canDeactivate on this route.
  hasUnsavedChanges(): boolean {
    return this.view === 'detail' && !!this.detail?.dirty;
  }
}
